import { Injectable, inject } from '@angular/core';
import { ValidationContextService } from './validation-context.service';
import { extractTaxonomyFromSchema } from './validation-taxonomy.util';
import { SHEET_KEYS } from '../../models/sheet-keys';
import { EqualityValidationError } from './equality-validation.service';
import { CrossSheetCalcDebugService } from './cross-sheet-calc-debug.service';
import { comparisonDifference, MBRS_EQUALITY_TOLERANCE, diffMeetsTolerance } from './validation-field-key.util';
// NOTE: The ConsecutiveYearCashValidation rule-config interface now lives in
// ../../models/validation-rules.model. It is not imported here because this
// service reads the cash rule via untyped `control.validation.consecutiveYearCash`
// (rule.comparisons), so the type is not referenced in code — the move is for
// documentation/colocation only.

/**
 * CashFlowReconciliationService
 *
 * Owns the consecutive-year cash balance continuity validation. Relocated
 * verbatim from the legacy combined validation-warning service as part of the
 * validation-warning split; sheet-key resolution is rewired to
 * ValidationContextService.
 */
@Injectable({
  providedIn: 'root'
})
export class CashFlowReconciliationService {
  private ctx = inject(ValidationContextService);
  private crossSheetCalcDebug = inject(CrossSheetCalcDebugService);

  /**
   * Validate consecutive year cash balance continuity
   * Compares current year beginning cash with previous year ending cash
   * Checks against both End and End_occurrence2 fields
   * Only runs if financial years are consecutive (no gap)
   *
   * NOTE: `CashAndCashEquivalents:End_occurrence2` is a calculated field
   * (`= CashAndBankBalances - BankOverdraft + OtherAdjustments`). Callers MUST
   * run the row-total calculation pass and write the latest values back to
   * the form/sheet snapshot BEFORE invoking this method. Otherwise this
   * validator compares a stale `End_occurrence2` against `Beginning` and
   * either misses real mismatches or surfaces spurious ones. The editor flow
   * already honours this contract via `runAllValidations()`'s ordering
   * (`calculateTotals()` → `runEqualityValidations()`); any future caller
   * (pure validator path, parity-check harness) must do the same.
   *
   * @param schema - Form schema containing validation rules
   * @param formValues - All form values from all sheets; the caller is
   *                     responsible for ensuring the calculated
   *                     `End_occurrence2` cells inside this snapshot
   *                     reflect the latest computed value.
   * @param currentTaxonomy - Current taxonomy (MFRS or MPERS)
   * @param currentSheetKey - Current sheet key (usually "statementOfCashFlowsIndirectMethod")
   * @returns Object with qname keys and error arrays
   */
  validateConsecutiveYearCash(
    schema: any,
    formValues: any,
    currentTaxonomy: 'MFRS' | 'MPERS',
    currentSheetKey?: string
  ): { [qname: string]: EqualityValidationError[] } {
    const errors: { [qname: string]: EqualityValidationError[] } = {};

    if (!schema || !schema.controls) {
      return errors;
    }

    // Extract schema's taxonomy
    const schemaTaxonomy = extractTaxonomyFromSchema(schema);

    // Skip if schema taxonomy doesn't match current taxonomy
    if (!schemaTaxonomy || schemaTaxonomy !== currentTaxonomy) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Schema taxonomy (${schemaTaxonomy}) doesn't match current taxonomy (${currentTaxonomy}) - skipping consecutive year cash validation`);
      return errors;
    }

    // STEP 1: Check if years are consecutive
    // Get dates from DEI (Role-1: documentAndEntityInformation)
    // DEI data lives under 'filingPrerequisites' in Phase 1 (Role-0) and
    // under 'filingInfo' in Phase 2 (Role-1). Merge both so this validation
    // runs in either phase. The date-presence check below catches the empty case.
    const deiData: Record<string, any> = {
      ...(formValues[SHEET_KEYS.filingPrerequisites] ?? {}),
      ...(formValues[SHEET_KEYS.filingInfo] ?? {}),
    };
    if (!deiData) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ No DEI data found - skipping consecutive year cash validation');
      return errors;
    }

    // Get date fields based on taxonomy
    const currentYearStartField = 'ssmt-dei:CompanyCurrentFinancialYearStartDate';
    const previousYearEndField = 'ssmt-dei:CompanyPreviousFinancialYearEndDate';

    const currentYearStartDateStr = deiData[currentYearStartField];
    const previousYearEndDateStr = deiData[previousYearEndField];

    if (!currentYearStartDateStr || !previousYearEndDateStr) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Date fields not found in DEI - skipping consecutive year cash validation');
      return errors;
    }

    // Parse dates (format: YYYY-MM-DD)
    const currentYearStartDate = new Date(currentYearStartDateStr);
    const previousYearEndDate = new Date(previousYearEndDateStr);

    // Check if dates are valid
    if (isNaN(currentYearStartDate.getTime()) || isNaN(previousYearEndDate.getTime())) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log('⏭️ Invalid date format - skipping consecutive year cash validation');
      return errors;
    }

    // Calculate the day after previous year end date
    const dayAfterPreviousYearEnd = new Date(previousYearEndDate);
    dayAfterPreviousYearEnd.setDate(dayAfterPreviousYearEnd.getDate() + 1);

    // Compare dates (check if current year start is the day after previous year end)
    const isConsecutive = currentYearStartDate.getTime() === dayAfterPreviousYearEnd.getTime();

    if (!isConsecutive) {
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log(`⏭️ Years are not consecutive - skipping cash validation`, {
      //   previousYearEnd: previousYearEndDateStr,
      //   currentYearStart: currentYearStartDateStr,
      //   dayAfterPreviousYearEnd: dayAfterPreviousYearEnd.toISOString().split('T')[0]
      // });
      return errors;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(`✅ Years are consecutive - running consecutive year cash validation`);

    // STEP 2: Find controls with consecutiveYearCash validation
    schema.controls.forEach((control: any) => {
      if (!control.validation?.consecutiveYearCash) return;

      const rule = control.validation.consecutiveYearCash;
      const comparisons = rule.comparisons;
      if (!comparisons || comparisons.length === 0) return;

      const sheetData = currentSheetKey ? formValues[currentSheetKey] : formValues[this.ctx.resolveSheetKey(rule.sheet) ?? rule.sheet];
      if (!sheetData) return;

      const tolerance = rule.tolerance ?? MBRS_EQUALITY_TOLERANCE;

      // Spec-exact labels. The schema row labels are lowercase
      // ("Cash and cash equivalents at end of period") and the sheet uses the
      // plural ("Cash Flows"); the requirement specifies uppercase
      // "End of Period" / "beginning of Period" and singular "Cash Flow",
      // so the wording is hardcoded here rather than mutating the schema
      // labels (which drive the row labels and other surfaces too).
      const beginningLabel = 'Cash and cash equivalents at beginning of Period';
      const endLabel = 'Cash and cash equivalents at End of Period';
      const cashSheetLabel = 'Statement of Cash Flow (Indirect Method)';

      // Group comparisons by consolidationType so we can:
      //   - skip the consolidation when Beginning + every comparison field is null
      //   - emit at most ONE error on the Beginning row per consolidation, even
      //     when both End and End_occurrence2 fail (their messages are identical)
      const compsByConsolidation = new Map<string, any[]>();
      comparisons.forEach((comp: any) => {
        const key = comp.beginningConsolidationType;
        if (!compsByConsolidation.has(key)) compsByConsolidation.set(key, []);
        compsByConsolidation.get(key)!.push(comp);
      });

      compsByConsolidation.forEach((comps, consolidationType) => {
        // Read Beginning value once
        const beginKey = `${control.qname}[${consolidationType}][CurrentYear]`;
        const beginRaw = sheetData[beginKey];
        const beginNumRaw = (beginRaw !== null && beginRaw !== undefined && beginRaw !== '')
          ? Number(beginRaw) : null;

        // Read every comparison field's raw value up-front so we can decide
        // whether to skip the whole consolidation (all-null) before doing work.
        const compEvaluations = comps.map((comp: any) => {
          const compareField = comp.compareField;
          const endKey = `${compareField}[${consolidationType}][PreviousYear]`;
          const endRaw = sheetData[endKey];
          const endNumRaw = (endRaw !== null && endRaw !== undefined && endRaw !== '')
            ? Number(endRaw) : null;
          return { compareField, endNumRaw };
        });

        // Skip ONLY when Beginning AND every comparison field is null/empty
        // (per spec: "do not display when all involved fields are null").
        const beginIsNull = beginNumRaw === null || isNaN(beginNumRaw as number);
        const allEndsNull = compEvaluations.every(evaluation =>
          evaluation.endNumRaw === null || isNaN(evaluation.endNumRaw as number)
        );
        if (beginIsNull && allEndsNull) return;

        // Treat null as 0 so a missing roll-forward on either side surfaces
        // (per spec: "display error message when one of the involved field is null or 0").
        const beginForCompare = beginIsNull ? 0 : (beginNumRaw as number);

        let firstFailedField: string | null = null;
        let firstFailedDiff = 0;

        // Each comparison is evaluated independently. A failure pushes an
        // error onto its own End / End_occurrence2 row (per Gap A: AND-style,
        // not OR-style — fixing the prior implementation that swallowed
        // errors when any one comparison happened to match).
        compEvaluations.forEach(({ compareField, endNumRaw }) => {
          const endIsNull = endNumRaw === null || isNaN(endNumRaw as number);
          const endForCompare = endIsNull ? 0 : (endNumRaw as number);
          // Round both operands to 2 d.p. before diffing (shared policy).
          const diff = comparisonDifference(beginForCompare, endForCompare);

          // Diagnostic only (no-op unless RuntimeConfig.crossSheetCalcDebug).
          this.crossSheetCalcDebug.log({
            source: 'cash-flow-reconciliation:consecutiveYear',
            currentSheet: rule.sheet,
            currentField: `${control.qname} (Beginning, ${consolidationType} Current)`,
            compareSheet: rule.sheet,
            compareField: `${compareField} (End, ${consolidationType} Previous)`,
            column: consolidationType,
            currentRaw: beginRaw,
            compareRaw: endIsNull ? null : endForCompare,
            tolerance,
            rounds: true,
          });

          // Within one cent = a match → skip. (Old `diff <= tolerance` counted a
          // one-cent-off End as a match, suppressing a true mismatch.)
          if (!diffMeetsTolerance(diff, tolerance)) return;

          if (firstFailedField === null) {
            firstFailedField = compareField;
            firstFailedDiff = diff;
          }

          if (!errors[compareField]) { errors[compareField] = []; }
          errors[compareField].push({
            severity: 'ERROR',
            message: `${consolidationType} Previous: Mismatch in value provided for the same field. The value should match ${beginningLabel} (${consolidationType} Current Year) in ${cashSheetLabel}.`,
            type: 'equality',
            compareWith: { sheet: rule.sheet, field: control.qname },
            difference: diff,
            tolerance: tolerance
          });
        });

        // Beginning row: a single error when ANY comparison fails
        // (per Gap B clarification: one error on Beginning, regardless of
        // whether one or both End rows failed — End and End_occurrence2
        // share the same label, so multiple entries would be visually
        // identical).
        if (firstFailedField !== null) {
          if (!errors[control.qname]) { errors[control.qname] = []; }
          errors[control.qname].push({
            severity: 'ERROR',
            message: `${consolidationType} Current: Mismatch in value provided for the same field. The value should match ${endLabel} (${consolidationType} Previous Year) in ${cashSheetLabel}.`,
            type: 'equality',
            compareWith: { sheet: rule.sheet, field: firstFailedField },
            difference: firstFailedDiff,
            tolerance: tolerance
          });
        }
      });
    });

    return errors;
  }
}
